import React, { useCallback, useState } from 'react';
import {
  Card,
  BlockStack,
  InlineStack,
  Text,
  Button,
  Banner,
  Divider,
  Spinner,
  Box,
} from '@shopify/polaris';
import { FieldDiff } from './FieldDiff';
import { ApprovalToggle } from './ApprovalToggle';
import {
  useFixPreviews,
  useGeneratePreviews,
  useUpdatePreview,
  useApplyFixes,
} from '../../hooks/useAutoFix';
import { useSubscriptionStatus } from '../../hooks/useSubscription';
import { LockedFeatureCard } from '../LockedFeatureCard';
import { LlmUsageIndicator } from '../LlmUsageIndicator';
import type { FixPreviewDto } from '../../types';

/**
 * Shopify product-edit field order (top → bottom).
 * Fields not listed here sort to the end alphabetically.
 */
const FIELD_SORT_ORDER: Record<string, number> = {
  title: 0,
  body_html: 1,
  image_alt_text: 2,
  product_type: 3,
  vendor: 4,
  tags: 5,
  metafields: 6,
  seo_title: 7,
  seo_description: 8,
  json_ld: 9,
};

function sortByShopifyFieldOrder(a: FixPreviewDto, b: FixPreviewDto): number {
  const orderA = FIELD_SORT_ORDER[a.field] ?? 100;
  const orderB = FIELD_SORT_ORDER[b.field] ?? 100;
  if (orderA !== orderB) return orderA - orderB;
  return a.field.localeCompare(b.field);
}

/**
 * T065: AutoFixPreview — orchestrates FieldDiff + ApprovalToggle list,
 * "Apply Fixes" button, and score delta display (FR-002, FR-010).
 */

interface AutoFixPreviewProps {
  readonly productId: string;
  readonly currentScore?: number;
}

export function AutoFixPreview({
  productId,
  currentScore,
}: AutoFixPreviewProps): React.JSX.Element {
  const { data: subscriptionStatus } = useSubscriptionStatus();
  const { data, isLoading: previewsLoading } = useFixPreviews(productId);
  const generateMutation = useGeneratePreviews();
  const updateMutation = useUpdatePreview();
  const applyMutation = useApplyFixes();

  const [applyingAll, setApplyingAll] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [editValues, setEditValues] = useState<Map<string, string>>(new Map());

  const rawPreviews = data?.previews ?? [];
  const previews = [...rawPreviews].sort(sortByShopifyFieldOrder);
  const approvedCount = previews.filter((p) => p.status === 'APPROVED').length;
  const pendingCount = previews.filter((p) => p.status === 'PENDING').length;
  const lowConfPendingCount = previews.filter(
    (p) => p.status === 'PENDING' && (p.confidence ?? 0) < 0.7,
  ).length;
  const highConfPendingCount = pendingCount - lowConfPendingCount;

  const handleGenerate = useCallback(() => {
    generateMutation.mutate({ productId });
  }, [generateMutation, productId]);

  const handleToggleSelect = useCallback((id: string, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  const handleEditChange = useCallback((id: string, value: string) => {
    setEditValues((prev) => {
      const next = new Map(prev);
      if (value) next.set(id, value);
      else next.delete(id);
      return next;
    });
  }, []);

  const handleReject = useCallback(
    (previewId: string) => {
      updateMutation.mutate({ previewId, productId, status: 'REJECTED' });
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(previewId);
        return next;
      });
    },
    [updateMutation, productId],
  );

  /** Approve checked fields and apply them to Shopify, then rescore. */
  const handleApproveSelected = useCallback(async () => {
    if (selectedIds.size === 0) return;
    setApplyingAll(true);
    try {
      for (const id of selectedIds) {
        const merchantEdit = editValues.get(id) || undefined;
        await updateMutation.mutateAsync({ previewId: id, productId, status: 'APPROVED', merchantEdit });
      }
      await applyMutation.mutateAsync({ productId });
      setSelectedIds(new Set());
    } catch {
      // Errors already surfaced via toast
    } finally {
      setApplyingAll(false);
    }
  }, [selectedIds, editValues, updateMutation, applyMutation, productId]);

  /** Approve all high-confidence (>= 70%) pending suggestions and apply them. */
  const handleApplyAll = useCallback(async () => {
    setApplyingAll(true);
    try {
      const pending = previews.filter(
        (p) => p.status === 'PENDING' && (p.confidence ?? 0) >= 0.7,
      );
      for (const p of pending) {
        const merchantEdit = editValues.get(p.id) || undefined;
        await updateMutation.mutateAsync({ previewId: p.id, productId, status: 'APPROVED', merchantEdit });
      }
      await applyMutation.mutateAsync({ productId });
      setSelectedIds(new Set());
    } catch {
      // Errors already surfaced via toast
    } finally {
      setApplyingAll(false);
    }
  }, [previews, editValues, updateMutation, applyMutation, productId]);

  // ─── Tier gate — show locked card for FREE plan ────────────
  if (subscriptionStatus?.tier === 'FREE') {
    return (
      <LockedFeatureCard
        featureName="AI-Boost"
        requiredTier="STARTER"
        description="Generate AI-powered fix suggestions and apply them to your products with one click."
        source="product-detail-autofix"
      />
    );
  }

  // ─── No previews yet ─────────────────────────────────────
  if (!previewsLoading && previews.length === 0) {
    const wasAlreadyOptimized = generateMutation.data?.alreadyOptimized;
    return (
      <Card>
        <BlockStack gap="300">
          <InlineStack align="space-between" blockAlign="start">
            <InlineStack gap="200" blockAlign="center">
              <LlmUsageIndicator />
              <Text as="h2" variant="headingMd">AI-Boost Suggestions</Text>
            </InlineStack>
          </InlineStack>
          {wasAlreadyOptimized ? (
            <>
              <Banner tone="success">
                <Text as="p" variant="bodySm">
                  Your content quality is already well-optimised — our AI couldn&apos;t find
                  improvements that would raise the text quality score. Great work!
                </Text>
              </Banner>
              <Button
                onClick={handleGenerate}
                loading={generateMutation.isPending}
                size="slim"
              >
                Re-check Suggestions
              </Button>
            </>
          ) : (
            <>
              <Text as="p" variant="bodyMd" tone="subdued">
                Generate AI-powered fix suggestions to improve this product&apos;s score.
              </Text>
              <Button
                onClick={handleGenerate}
                loading={generateMutation.isPending}
                variant="primary"
              >
                AI-Boost All
              </Button>
            </>
          )}
        </BlockStack>
      </Card>
    );
  }

  return (
    <Card>
      <BlockStack gap="400">
        <InlineStack align="space-between" blockAlign="start">
          <InlineStack gap="200" align="start" blockAlign="center">
            <LlmUsageIndicator />
            <Text as="h2" variant="headingMd">AI-Boost Suggestions</Text>
          </InlineStack>
          <InlineStack gap="200" align="start" blockAlign="center">
            {currentScore != null && (
              <Text as="span" variant="bodySm" tone="subdued">
                Current score: {currentScore}
              </Text>
            )}
            <Button
              onClick={handleGenerate}
              loading={generateMutation.isPending}
              size="slim"
            >
              Regenerate
            </Button>
            {selectedIds.size > 0 && (
              <Button
                onClick={handleApproveSelected}
                loading={applyingAll}
                variant="primary"
                size="slim"
              >
                {`Approve Selected (${selectedIds.size})`}
              </Button>
            )}
            {(highConfPendingCount + approvedCount) > 0 && (
              <Button
                onClick={handleApplyAll}
                loading={applyingAll}
                size="slim"
              >
                Apply All & Rescore
              </Button>
            )}
          </InlineStack>
        </InlineStack>

        {previewsLoading && (
          <Box padding="400"><Spinner size="small" /></Box>
        )}

        {generateMutation.isError && (
          <Banner tone="critical">
            <Text as="p" variant="bodySm">
              Failed to generate previews: {generateMutation.error.message}
            </Text>
          </Banner>
        )}

        {applyMutation.isSuccess && (
          <Banner tone="success">
            <Text as="p" variant="bodySm">
              Fixes applied to Shopify and score updated.
            </Text>
          </Banner>
        )}

        {generateMutation.data?.alreadyOptimized && (
          <Banner tone="info">
            <Text as="p" variant="bodySm">
              Your text quality is already well-optimised. The suggestions below are for
              empty or missing fields only — applying them won&apos;t regress your score.
            </Text>
          </Banner>
        )}

        {lowConfPendingCount > 0 && !applyingAll && (
          <Banner tone="warning">
            <Text as="p" variant="bodySm">
              {lowConfPendingCount} suggestion{lowConfPendingCount > 1 ? 's' : ''} with low confidence
              {lowConfPendingCount > 1 ? ' are' : ' is'} excluded from &ldquo;Apply All &amp; Rescore&rdquo;. Check
              {lowConfPendingCount > 1 ? ' them' : ' it'} individually to include.
            </Text>
          </Banner>
        )}

        {/* Preview list */}
        {previews.map((preview: FixPreviewDto) => {
          const isRowSelected = selectedIds.has(preview.id) && preview.status === 'PENDING';
          return (
            <React.Fragment key={preview.id}>
              <Box
                background={isRowSelected ? 'bg-surface-selected' : undefined}
                borderInlineStartWidth={isRowSelected ? '050' : undefined}
                borderColor={isRowSelected ? 'border-brand' : undefined}
                paddingInlineStart={isRowSelected ? '400' : undefined}
                paddingBlock={isRowSelected ? '200' : undefined}
                borderRadius={isRowSelected ? '100' : undefined}
              >
                <BlockStack gap="200">
                  <FieldDiff
                    preview={preview}
                    editValue={editValues.get(preview.id)}
                    onEditChange={(value) => handleEditChange(preview.id, value)}
                  />
                  <ApprovalToggle
                    previewId={preview.id}
                    editValue={editValues.get(preview.id) ?? ''}
                    currentStatus={preview.status}
                    isSelected={isRowSelected}
                    onToggleSelect={handleToggleSelect}
                    onEditChange={handleEditChange}
                    onReject={handleReject}
                    isLoading={updateMutation.isPending || applyMutation.isPending || applyingAll}
                  />
                </BlockStack>
              </Box>
              <Divider />
            </React.Fragment>
          );
        })}

        {/* Summary counts */}
        {pendingCount > 0 && (
          <InlineStack align="end">
            <Text as="span" variant="bodySm" tone="subdued">
              {selectedIds.size > 0 ? `${selectedIds.size} selected, ` : ''}{pendingCount} pending
            </Text>
          </InlineStack>
        )}
      </BlockStack>
    </Card>
  );
}

export default AutoFixPreview;
